/**
 * SE-1765 T13: SPA-side wire helpers for the `simpl_auth` proxy Lambdas
 * (`api/known-roles`, `api/scope-choices`, `api/role-scopes`,
 * `api/get-user-access`, `api/user-access`).
 *
 * These do NOT talk to simpl_auth directly — the internal ALB is
 * VPC-only. They call the simpl-api Lambdas, which pin the caller
 * identity to the authorizer-verified email and forward to simpl_auth
 * with the T12 delegated-caller header. See
 * api/known-roles/src/index.ts for the security rationale.
 *
 * The response shapes here mirror the T6/T7 wire format exactly (the
 * proxy Lambdas pass simpl_auth's JSON through verbatim) so a future
 * consumer that reads a saved response has one schema to worry about,
 * not two. Do not remap field names to camelCase — the SPA is a
 * downstream of simpl_auth's contract, not the other way around.
 */
import { API } from "aws-amplify";

/**
 * One role's config as served by simpl_auth's T6 endpoint. `scope_axis`
 * drives the axis-per-page decision in the T13 skeletons (Rotations →
 * person_group, Coaching → person). `grantable_by_caller` is what a
 * "save" button should be gated on — a role visible in `roles` but
 * false here is the ❌ GA-3 UI-dead-end this endpoint exists to prevent.
 */
export type RoleConfig = {
  scope_axis: string | null;
  grantable_by_caller: boolean;
  has_computed_path: boolean;
};

export type KnownRolesResponse = {
  /** Every role in the catalog, in KNOWN_ROLES order. */
  roles: string[];
  role_config: Record<string, RoleConfig>;
  /**
   * Sorted, pre-filtered picker list for THIS caller. Equal to
   * `Object.keys(role_config).filter(r => role_config[r].grantable_by_caller)`
   * but sorted alphabetically. Serve-side pre-filtering exists so
   * pickers don't reimplement the predicate.
   */
  grantable_roles: string[];
  computed_path_roles: string[];
};

export type ScopeChoice = { id: string; label: string };

export type ScopeChoicesResponse = {
  axis: string;
  choices: ScopeChoice[];
};

/** The server's `{error}` text on a failed simpl-api call, else the generic message. */
export const apiErrorMessage = (err: unknown, fallback: string): string => {
  const data = (err as { response?: { data?: { error?: unknown } } })?.response
    ?.data;
  return typeof data?.error === "string" && data.error ? data.error : fallback;
};

export const fetchKnownRoles = async (): Promise<KnownRolesResponse> =>
  (await API.get("simpl-api", "known-roles", {})) as KnownRolesResponse;

export const fetchScopeChoices = async (input: {
  axis: string;
  programId?: string;
}): Promise<ScopeChoicesResponse> => {
  const queryStringParameters: Record<string, string> = { axis: input.axis };
  if (input.programId) queryStringParameters.program_id = input.programId;
  return (await API.get("simpl-api", "scope-choices", {
    queryStringParameters,
  })) as ScopeChoicesResponse;
};

/**
 * Pick out the roles catalogued on one axis. Used by SE-1882 Coaching
 * (person) and SE-1883 Rotations (person_group) to derive their role
 * pickers off the T6 catalog rather than hardcoding "Coach" /
 * "RotationDirector" — a role added to simpl_entitlements' catalog on
 * the same axis (design §11.2, EX-1) then appears in the picker with
 * zero client edits.
 *
 * Returns the roles catalog-order (as delivered by simpl_auth), so the
 * "primary" role for each page (the only one on its axis in today's
 * catalog) sorts first and can be defaulted without a client-side rule.
 */
export const rolesOnAxis = (
  catalog: KnownRolesResponse,
  axis: string,
): string[] =>
  catalog.roles.filter((role) => catalog.role_config[role]?.scope_axis === axis);

// =============================================================================
// SE-1882: role-scopes (READ) + user-access (READ/WRITE)
// =============================================================================
//
// The three wire helpers below let the Coaching page reconcile a
// coach's mentee bindings end-to-end:
//
//   listRoleScopes → the by-scope grants read (populates the
//                    coaches-per-program table).
//   fetchUserAccess → the by-email preserve-read (echoes the coach's
//                    current flat `roles` / `entitlements` on save
//                    so a Coach + ProgramAdmin isn't silently demoted
//                    to Coach-only — see api/get-user-access/src/index.ts
//                    for the write-path context).
//   putUserAccess  → the declarative write (the coach's whole mentee
//                    set for the Coach role, plus preserved flat
//                    grants).
//
// Field names track simpl_auth's wire verbatim. See
// api/lib/simpl-auth-api/index.ts's typedefs for the shared-through-
// hardlink source-of-truth version of the shapes; the SPA holds its
// own copy here because it does not import from `../../../../api/lib`
// (the two packages have separate build chains).

/**
 * One row from `GET /api/role-scopes`. Mirrors
 * `simpl_auth-se-1765/src/user_access.py:_role_scope_payload` — the
 * shared serializer used by the by-scope read AND the by-email read, so
 * one row cannot describe itself two ways depending on which door it
 * came out of. See api/role-scopes/src/index.ts for the security
 * rationale.
 *
 * PROVENANCE IS NOT ADVISORY (SE-1819 D3). `data_source_name` other
 * than `ADMIN` MUST render read-only in the SPA — the internal
 * template's `access_editor.split_by_provenance` splits on exactly this
 * predicate, and revoking a foreign-provenance row would just get
 * re-minted by the next sync.
 */
export type RoleScope = {
  email: string;
  /** Always "Coach" on the coaching page today. */
  role: string;
  /** Always "person" on coaching. */
  scope_type: string;
  scope_id: string;
  scope_name: string | null;
  /**
   * Provenance sentinel — `ADMIN`, `SIMULATED_SSO`, and friends.
   * NOT a user-facing label; render through a badge helper.
   */
  data_source_name: string | null;
  created_at: string;
  /**
   * Row-level assignment provenance — the operator email that
   * WROTE this row (the T12 caller or an internal-plane actor). NULL for
   * pre-migration rows and non-HTTP writers (CLIs, roster syncs).
   *
   * NOT RENDERED IN THE SIMPL-API COACHING PAGE. The end-user surface
   * stays focused on the people; the internal auth-admin dashboards
   * surface is where per-row operator provenance shows. Present here for
   * type-level wire parity with the server's `_role_scope_payload`, so a
   * future debugging / audit surface can pick it up without a schema
   * mismatch.
   */
  assigned_by_email: string | null;
  /**
   * Row-level source-UI provenance — one of the values in the
   * auth service's `KNOWN_CLIENTS` whitelist at write time
   * (`dashboards_admin`, `simpl_api_admin`, …). NULL for pre-migration
   * rows and non-HTTP writers. Same non-rendering rule as
   * `assigned_by_email`.
   */
  assigned_via_client: string | null;
};

export type RoleScopesResponse = {
  role_scopes: RoleScope[];
};

/**
 * `GET /api/get-user-access` — one user's flat grants plus their whole
 * role_scopes list across every axis. Used ONLY on save, to preserve
 * `roles` / `entitlements` verbatim through the declarative PUT
 * (see api/get-user-access/src/index.ts). NOT the coaching table's
 * data source — that's `listRoleScopes`, which narrows on the whole
 * program in one call.
 */
export type UserAccessGrants = {
  roles: string[];
  entitlements: string[];
  /**
   * DEPRECATED (SE-1776 Phase B) — the server accepts on write and
   * ignores. The SPA does NOT surface this in a picker.
   */
  orgs?: string[];
  notes?: string | null;
};

/**
 * simpl_auth's per-GRANTED-role verdict (`role_bindings_payload`): what
 * scopes the role at sign-in, and whether it drops there. Computed by the
 * same `bind_role_scope_ids` as the mint and the write-path refusal, so
 * the SPA never re-derives it.
 */
export type RoleBinding = {
  role: string;
  axis: string | null;
  scope_ids: string[];
  drops_at_mint: boolean;
};

export type UserAccessResponse = {
  email: string;
  grants: UserAccessGrants;
  role_scopes: RoleScope[];
  role_bindings?: RoleBinding[];
};

/**
 * One `(scope_id, scope_name)` binding for a role on some axis, as
 * simpl_auth expects it on the write. `scope_name` is denormalized so
 * the audit row carries a display label without a second lookup — see
 * `_role_scope_payload` on the server.
 */
export type ScopeBinding = {
  scope_id: string;
  scope_name: string;
};

export type PutUserAccessInput = {
  /**
   * The user whose access is being written (the coach on this page).
   * Travels in the BODY, not the query — simpl_auth's
   * `admin_put_user_access` docstring pins this in the first
   * body-envelope example.
   */
  email: string;
  /**
   * The coach's whole mentee set for Coach after the write. simpl_auth
   * diffs against current to compute add / archive.
   * ABSENT KEY = "don't touch that axis" (which is why this is
   * optional at the wire level — the coaching page always sends it,
   * but the shape supports future callers that manage flat grants
   * without touching person scopes).
   */
  person_scopes?: Record<string, ScopeBinding[]>;
  /**
   * The director's whole rotation set for RotationDirector after the
   * write. Same declarative idiom as `person_scopes`. Rotations
   * (SE-1883) always sends this; Coaching omits it.
   */
  person_group_scopes?: Record<string, ScopeBinding[]>;
};

/**
 * `PUT /api/user-access` returns `{"changed": bool}` — true when an
 * audit row was written. Surfaced verbatim so a SPA that races a save
 * against its own re-read can tell an actual mutation from a "your set
 * already matched" no-op.
 */
export type PutUserAccessResponse = {
  changed: boolean;
  /** Coach / RotationDirector labels the save added (SE-2302). */
  roles_assigned?: { program_id: string; role: string }[];
  /** Labels it ended: the person's last binding in that program went (SE-3027). */
  roles_ended?: { program_id: string; role: string }[];
};

export const fetchRoleScopes = async (input: {
  roles: string[];
  scopeType?: "person" | "person_group";
  programId?: string;
  scopeIds?: string[];
}): Promise<RoleScopesResponse> => {
  const queryStringParameters: Record<string, string> = {
    roles: input.roles.join(","),
  };
  if (input.scopeType) queryStringParameters.scope_type = input.scopeType;
  if (input.programId) queryStringParameters.program_id = input.programId;
  if (input.scopeIds && input.scopeIds.length > 0) {
    queryStringParameters.scope_ids = input.scopeIds.join(",");
  }
  return (await API.get("simpl-api", "role-scopes", {
    queryStringParameters,
  })) as RoleScopesResponse;
};

export const fetchUserAccess = async (
  email: string,
): Promise<UserAccessResponse> =>
  (await API.get("simpl-api", "get-user-access", {
    queryStringParameters: { email },
  })) as UserAccessResponse;

export const putUserAccess = async (
  input: PutUserAccessInput,
): Promise<PutUserAccessResponse> => {
  // No `roles` / `entitlements`: the Lambda computes them from the person's
  // current access (SE-3027), so the flat-role rule has one owner.
  const body: Record<string, unknown> = { email: input.email };
  if (input.person_scopes) body.person_scopes = input.person_scopes;
  if (input.person_group_scopes) {
    body.person_group_scopes = input.person_group_scopes;
  }
  return (await API.put("simpl-api", "user-access", {
    body,
  })) as PutUserAccessResponse;
};

// =============================================================================
// SE-1882: coach-centric grouping of role-scopes rows.
// =============================================================================
//
// The coaching TABLE renders one row per coach — "who does this coach
// mentor?" (Brian, 2026-08-08, §12.1: "coaches and their mentees").
// Mirrors `coaching_service.group_by_coach` in
// `simpl_dashboards-se-1765/app/src/system_app/pages/auth_admin/services/coaching_service.py`
// so the internal auth-admin UI and this admin site cannot drift on
// what "one coach" means.

export type Coach = {
  /** Display casing of the email (first-seen casing wins). */
  email: string;
  /** Sorted distinct roles this coach holds bindings for (["Coach"] today). */
  roles: string[];
  /**
   * The coach's rows, sorted by (scope_name or scope_id) case-
   * insensitively so the list reads alphabetically even when a name is
   * missing and the id stands in.
   */
  mentees: RoleScope[];
  /**
   * Sorted distinct provenance labels (raw `data_source_name` values).
   * The SPA renders these through a badge helper — this list is
   * unchanged from the wire so the badge helper is the ONE place that
   * maps `SIMULATED_SSO` → "Simulated SSO" etc.
   */
  sources: string[];
};

/**
 * Fold role-scope rows into one entry per coach. Email casing merges
 * case-insensitively (two casings of one address are one person). Pure,
 * total, no I/O — same shape as the internal template's
 * `group_by_coach` so a test can pin the two implementations against
 * one fixture.
 */
export const groupByCoach = (rows: RoleScope[]): Coach[] => {
  const grouped: Record<string, {
    email: string;
    roles: Set<string>;
    mentees: RoleScope[];
    sources: Set<string>;
  }> = {};

  for (const row of rows) {
    const email = row.email ?? "";
    const key = email.toLowerCase();
    if (!grouped[key]) {
      grouped[key] = {
        email,
        roles: new Set<string>(),
        mentees: [],
        sources: new Set<string>(),
      };
    }
    const entry = grouped[key];
    if (row.role) entry.roles.add(row.role);
    entry.mentees.push(row);
    if (row.data_source_name) entry.sources.add(row.data_source_name);
  }

  const entries = Object.entries(grouped);
  entries.sort(([a], [b]) => a.localeCompare(b));

  return entries.map(([, entry]) => ({
    email: entry.email,
    roles: Array.from(entry.roles).sort(),
    mentees: [...entry.mentees].sort((a, b) => {
      const labelA = (a.scope_name ?? a.scope_id ?? "").toLowerCase();
      const labelB = (b.scope_name ?? b.scope_id ?? "").toLowerCase();
      return labelA.localeCompare(labelB);
    }),
    sources: Array.from(entry.sources).sort(),
  }));
};

/**
 * Is this grant row ours to change? Mirrors
 * `access_editor.is_admin_owned` on the internal side — SE-1819 D3
 * keeps grants provenance-locked, so a non-ADMIN row on the client
 * MUST render read-only (revoke a synced binding and the next sync
 * re-mints it).
 *
 * ADMIN and legacy-NULL are ours; anything else was written by a named
 * system whose next sync would restore it. Kept as a one-liner so the
 * SPA can inline it in a JSX `disabled` prop without an import cycle.
 */
export const isAdminOwned = (dataSourceName: string | null): boolean =>
  dataSourceName === "ADMIN" || dataSourceName === null;

/**
 * SE-2841: the outbound declarative set for ONE role on ONE axis after a
 * single add or remove — the person's CURRENT ADMIN bindings, minus
 * `remove`, plus `add`.
 *
 * `roleScopes` MUST be the person's own `fetchUserAccess` read, taken at
 * save time: it is the only read that spans every program and is fresh.
 * The PUT is declarative per axis, so any ADMIN binding missing here is
 * ARCHIVED — a by-program list drops the person's bindings in other
 * programs, and a page-load snapshot drops whatever landed since.
 *
 * Its rows carry no `email` (the envelope does), so they are never
 * matched by email: filtering them that way emptied the current set and
 * made every new directorship replace the old ones (SE-2841).
 *
 * Foreign-provenance rows are never in the set (SE-1819 D3) — the
 * server leaves them alone either way.
 */
export const nextScopeSet = (
  roleScopes: RoleScope[],
  role: string,
  scopeType: string,
  change: { add?: ScopeBinding[]; remove?: string[] },
): ScopeBinding[] => {
  const removed = new Set(change.remove ?? []);
  const out = new Map<string, ScopeBinding>();
  for (const row of roleScopes) {
    if (
      row.role !== role ||
      row.scope_type !== scopeType ||
      !row.scope_id ||
      !isAdminOwned(row.data_source_name) ||
      removed.has(row.scope_id)
    ) {
      continue;
    }
    out.set(row.scope_id, {
      scope_id: row.scope_id,
      scope_name: row.scope_name ?? row.scope_id,
    });
  }
  for (const binding of change.add ?? []) {
    if (!out.has(binding.scope_id)) out.set(binding.scope_id, binding);
  }
  return Array.from(out.values());
};

// =============================================================================
// SE-1883: director-centric grouping of role-scopes rows.
// =============================================================================
//
// Same fold as `groupByCoach`, different reader labels. Kept as a
// rename rather than a generic `groupByPrincipal` because the two
// lists' next divergence — mentee names vs rotation members — reads
// out of the binding differently and will not stay identical.

export type Director = {
  email: string;
  roles: string[];
  rotations: RoleScope[];
  sources: string[];
};

export const groupByDirector = (rows: RoleScope[]): Director[] =>
  groupByCoach(rows).map((coach) => ({
    email: coach.email,
    roles: coach.roles,
    rotations: coach.mentees,
    sources: coach.sources,
  }));

// =============================================================================
// SE-1883: person-groups plane — rotations CRUD + membership.
// =============================================================================
//
// The six wrappers below drive the Rotations page's non-grant surface:
// list rotations, create / rename / archive them, list and edit their
// members. They call the simpl-api proxy Lambdas
// (`api/{list,create,patch,get,patch,get}-person-group[s|-members|
// -member-candidates]/`), which pin the caller identity to the
// authorizer-verified email and forward to simpl_auth's
// `/api/internal/person-groups*`.
//
// SHAPE PARITY: wire types mirror `simpl_auth-se-1765/src/person_groups.py`
// and `api/lib/simpl-auth-api/index.ts` verbatim. The SPA is a downstream
// of that contract; renames belong in the service, not here.
//
// TWO CONTRACTS TO REMEMBER:
//   1. `archived` on `PATCH /person-groups/{id}` is destructive. The caller
//      MUST revoke the ADMIN director grants FIRST (while the group is
//      still an offered scope choice), THEN patch archived=true. Once
//      archived, `scope-choices` drops the group and the binding can no
//      longer be addressed. See `services/rotations_service.py` in the
//      internal template.
//   2. Member writes use `patchPersonGroupMembers` (NAMED add/remove).
//      There is NO SPA function for the declarative `PUT /members` —
//      `domain/person-group` inv. 4a disallows it on interactive surfaces
//      (SE-1819: absence-as-verb archived 30 sync-owned members).

/** One rotation row as returned by GET / POST / PATCH person-groups. */
export type PersonGroup = {
  id: string;
  program_id: string;
  label: string;
  /** ISO-8601 string when archived, null when active. */
  archived_at: string | null;
  /** Entry constraint on future member adds. `null` = unrestricted. */
  member_roles: string[] | null;
  /**
   * SE-2832: the one network of its program the rotation belongs to, or
   * null for the whole program. A director who does not administer the
   * program sees the rotation only in that network's dashboard.
   */
  network_id?: string | null;
  /** Present only on the LIST payload; counts ACTIVE members. */
  member_count?: number;
};

/** A network the program sits under: what a rotation's network may be. */
export type ProgramNetwork = { id: string; name: string | null };

export type PersonGroupsListResponse = {
  groups: PersonGroup[];
  /**
   * SE-2832: the program's networks, from the table simpl_auth checks a
   * network against. Absent from a server predating the field.
   */
  networks?: ProgramNetwork[];
};

export type PersonGroupMember = {
  person_id: string;
  first_name: string | null;
  last_name: string | null;
  person_role: string | null;
  /**
   * Provenance of the MEMBERSHIP row (NOT the person). NULL for admin
   * writes; a non-null value names a synchronisation source. ADVISORY:
   * per SE-1819 D3, the SPA does NOT hide the Remove control on a
   * sync-written membership — it surfaces the source and lets removal
   * proceed with the disclosure that the next sync may write it back.
   * A DIRECTORSHIP with non-ADMIN provenance goes the other way —
   * revoke-hidden. This is the two-controls-opposite-rules rule that
   * lives on this page.
   */
  data_source_name: string | null;
};

export type PersonGroupMembersResponse = {
  id: string;
  program_id: string;
  members: PersonGroupMember[];
};

export type PersonGroupCandidate = {
  person_id: string;
  first_name: string | null;
  last_name: string | null;
  person_role: string | null;
};

export type PersonGroupCandidatesResponse = {
  id: string;
  program_id: string;
  candidates: PersonGroupCandidate[];
};

/**
 * Response from the NAMED-delta PATCH /members. `not_found` names remove
 * ids that were already not active members — a stale-view signal, not an
 * error. The SPA surfaces it as "N of the people you removed were already
 * not members" per the internal-template convention.
 */
export type PersonGroupMemberDeltaResponse = {
  id: string;
  program_id: string;
  added: number;
  archived: number;
  member_count: number;
  not_found: string[];
};

export const listPersonGroups = async (input: {
  programId: string;
  rosterRoles?: string[];
}): Promise<PersonGroupsListResponse> => {
  const queryStringParameters: Record<string, string> = {
    program_id: input.programId,
  };
  if (input.rosterRoles && input.rosterRoles.length > 0) {
    queryStringParameters.roster_roles = input.rosterRoles.join(",");
  }
  return (await API.get("simpl-api", "person-groups", {
    queryStringParameters,
  })) as PersonGroupsListResponse;
};

export const createPersonGroup = async (input: {
  programId: string;
  label: string;
  memberRoles?: string[];
  networkId?: string | null;
}): Promise<PersonGroup> => {
  const body: Record<string, unknown> = {
    program_id: input.programId,
    label: input.label,
  };
  // Rotation members are Attendings only: the Lambda defaults to and
  // accepts only `["Attending"]` (422 otherwise).
  if (input.memberRoles !== undefined) body.member_roles = input.memberRoles;
  // SE-2832: only a chosen network is sent; none means the whole program.
  if (input.networkId) body.network_id = input.networkId;
  return (await API.post("simpl-api", "person-groups", {
    body,
  })) as PersonGroup;
};

export const patchPersonGroup = async (
  groupId: string,
  input: {
    label?: string;
    archived?: boolean;
    memberRoles?: string[];
    /** SE-2832: undefined leaves it, null clears to the whole program. */
    networkId?: string | null;
  },
): Promise<PersonGroup> => {
  const body: Record<string, unknown> = {};
  if (input.label !== undefined) body.label = input.label;
  if (input.archived !== undefined) body.archived = input.archived;
  if (input.memberRoles !== undefined) body.member_roles = input.memberRoles;
  if (input.networkId !== undefined) body.network_id = input.networkId;
  return (await API.patch("simpl-api", `person-groups/${groupId}`, {
    body,
  })) as PersonGroup;
};

export const fetchPersonGroupMembers = async (
  groupId: string,
): Promise<PersonGroupMembersResponse> =>
  (await API.get(
    "simpl-api",
    `person-groups/${groupId}/members`,
    {},
  )) as PersonGroupMembersResponse;

/**
 * NAMED add/remove delta on membership (SE-1819). This is the interactive
 * door. Absence is NEVER the verb here — removing means naming an id in
 * `remove`. Do NOT use a declarative PUT — that plane is not exposed.
 */
export const patchPersonGroupMembers = async (
  groupId: string,
  input: { add?: string[]; remove?: string[] },
): Promise<PersonGroupMemberDeltaResponse> =>
  (await API.patch("simpl-api", `person-groups/${groupId}/members`, {
    body: {
      add: input.add ?? [],
      remove: input.remove ?? [],
    },
  })) as PersonGroupMemberDeltaResponse;

export const fetchPersonGroupMemberCandidates = async (
  groupId: string,
  input: { roles?: string[] } = {},
): Promise<PersonGroupCandidatesResponse> => {
  const queryStringParameters: Record<string, string> = {};
  if (input.roles && input.roles.length > 0) {
    queryStringParameters.roles = input.roles.join(",");
  }
  return (await API.get(
    "simpl-api",
    `person-groups/${groupId}/member-candidates`,
    { queryStringParameters },
  )) as PersonGroupCandidatesResponse;
};

/** Two rows for the same person collapsed to one — the "twice in the
 *  roster appears once in every picker" acceptance criterion. Ordered by
 *  (last, first, person_id) case-insensitively so pickers read
 *  alphabetically. Preserves the row that has non-null names when either
 *  duplicate lacks them. */
export const dedupePeople = <T extends {
  person_id: string;
  first_name: string | null;
  last_name: string | null;
}>(rows: T[]): T[] => {
  const seen = new Map<string, T>();
  for (const row of rows) {
    const existing = seen.get(row.person_id);
    if (!existing) {
      seen.set(row.person_id, row);
      continue;
    }
    // Prefer the row with populated names — the null one is round-trip
    // safety, the named one is what the picker actually shows.
    if (!existing.first_name && !existing.last_name) {
      seen.set(row.person_id, row);
    }
  }
  const nameKey = (r: T) =>
    `${(r.last_name ?? "").toLowerCase()} ${(r.first_name ?? "").toLowerCase()} ${r.person_id}`;
  return Array.from(seen.values()).sort((a, b) =>
    nameKey(a).localeCompare(nameKey(b)),
  );
};

/**
 * Display name for a person the picker knows by parts. Never falls
 * through to the uuid — pickers list REAL people, and a bare uuid is a
 * bug or a vanished person_dim_v1 row (see the members read's
 * round-trip-safety comment). Missing names show as "Unknown person" so
 * the caller can tell "vanished" apart from "empty picker".
 */
export const personDisplayName = (person: {
  first_name: string | null;
  last_name: string | null;
}): string => {
  const first = (person.first_name ?? "").trim();
  const last = (person.last_name ?? "").trim();
  if (!first && !last) return "Unknown person";
  return `${first} ${last}`.trim();
};
